import React, { useState, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Star, BarChart2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ExerciseProgressionGraphProps {
  exerciseId: string;
  exerciseName: string;
  /** Which mode to show first. If the exercise has data in both modes, a toggle appears. */
  initialMode?: 'overload' | 'hypertrophy';
}

type TrainingMode = 'overload' | 'hypertrophy';

// Custom dot: renders a star for PR data points, a normal circle otherwise
const CustomDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (!cx || !cy) return null;
  if (payload?.isPR) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={8} fill="#f59e0b" opacity={0.25} />
        <Star
          x={cx - 7}
          y={cy - 7}
          width={14}
          height={14}
          fill="#f59e0b"
          stroke="#f59e0b"
          strokeWidth={1}
        />
      </g>
    );
  }
  return <circle cx={cx} cy={cy} r={3.5} fill={props.stroke} strokeWidth={0} />;
};

const CustomTooltip = ({
  active,
  payload,
  label,
  mode,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
  mode: TrainingMode;
}) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-background border border-border rounded-sm px-3 py-2 text-xs font-mono shadow-lg">
      <div className="text-muted-foreground mb-1">{label}</div>
      <div className="text-foreground font-bold">
        {mode === 'overload'
          ? `${d.value} LBS (est. 1RM)`
          : `${d.value.toLocaleString()} LBS vol`}
      </div>
      <div className="text-muted-foreground">
        {d.bestWeight} LBS × {d.bestReps} REPS
      </div>
      {d.isPR && (
        <div className="text-amber-400 mt-0.5">★ PR SESSION</div>
      )}
    </div>
  );
};

export default function ExerciseProgressionGraph({
  exerciseId,
  exerciseName,
  initialMode = 'overload',
}: ExerciseProgressionGraphProps) {
  const [mode, setMode] = useState<TrainingMode>(initialMode);

  const { data: overloadData = [], isLoading: loadingOverload } =
    trpc.prs.getExerciseProgression.useQuery(
      { exerciseId, trainingMode: 'overload' },
      { staleTime: 30_000 }
    );

  const { data: hypertrophyData = [], isLoading: loadingHypertrophy } =
    trpc.prs.getExerciseProgression.useQuery(
      { exerciseId, trainingMode: 'hypertrophy' },
      { staleTime: 30_000 }
    );

  const hasOverload = overloadData.length >= 2;
  const hasHypertrophy = hypertrophyData.length >= 2;
  const hasBoth = hasOverload && hasHypertrophy;

  const activeData = mode === 'overload' ? overloadData : hypertrophyData;
  const isLoading = mode === 'overload' ? loadingOverload : loadingHypertrophy;

  // Stats summary
  const stats = useMemo(() => {
    if (activeData.length === 0) return null;
    const best = activeData.reduce((max, d) => (d.value > max.value ? d : max), activeData[0]);
    const last3 = activeData.slice(-3);
    let trend: 'up' | 'flat' | 'down' = 'flat';
    if (last3.length >= 2) {
      const first = last3[0].value;
      const last = last3[last3.length - 1].value;
      const delta = last - first;
      const pct = first > 0 ? delta / first : 0;
      if (pct > 0.01) trend = 'up';
      else if (pct < -0.01) trend = 'down';
    }
    return { best, sessions: activeData.length, trend };
  }, [activeData]);

  const lineColor = mode === 'overload' ? '#22c55e' : '#f59e0b';
  const modeLabel = mode === 'overload' ? 'OVERLOAD' : 'HYPERTROPHY';

  // Format date labels compactly
  const formatDate = (d: string) => {
    const dt = new Date(d + 'T00:00:00');
    return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate()}`;
  };

  return (
    <div className="space-y-3">
      {/* Exercise name + mode toggle */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart2 className="h-4 w-4 text-primary shrink-0" />
          <span className="font-bold text-sm text-foreground">{exerciseName}</span>
        </div>
        {hasBoth && (
          <div className="flex items-center gap-1 bg-secondary/50 rounded-sm p-0.5">
            <button
              onClick={() => setMode('overload')}
              className={`px-2 py-0.5 text-[10px] font-mono rounded-sm transition-colors ${
                mode === 'overload'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              OVERLOAD
            </button>
            <button
              onClick={() => setMode('hypertrophy')}
              className={`px-2 py-0.5 text-[10px] font-mono rounded-sm transition-colors ${
                mode === 'hypertrophy'
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              HYPERTROPHY
            </button>
          </div>
        )}
        {!hasBoth && (
          <Badge
            className={`font-mono text-[9px] uppercase ${
              mode === 'overload'
                ? 'bg-green-500/20 border-green-500/40 text-green-400'
                : 'bg-amber-500/20 border-amber-500/40 text-amber-400'
            }`}
          >
            {modeLabel}
          </Badge>
        )}
      </div>

      {/* Chart area */}
      {isLoading ? (
        <div className="h-[180px] flex items-center justify-center">
          <div className="text-xs font-mono text-muted-foreground animate-pulse uppercase">Loading...</div>
        </div>
      ) : activeData.length < 2 ? (
        <div className="h-[120px] flex flex-col items-center justify-center border border-dashed border-border/50 rounded-sm bg-secondary/10 px-4">
          <TrendingUp className="h-6 w-6 text-muted-foreground/30 mb-2" />
          <p className="text-xs font-mono text-muted-foreground text-center uppercase">
            Not enough data yet
          </p>
          <p className="text-[10px] text-muted-foreground/70 text-center mt-1">
            Log 2 or more {modeLabel.toLowerCase()} sessions to see your progression.
          </p>
        </div>
      ) : (
        <div className="h-[180px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={activeData}
              margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
                width={48}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v))}
              />
              <Tooltip
                content={<CustomTooltip mode={mode} />}
                cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={lineColor}
                strokeWidth={2}
                dot={<CustomDot stroke={lineColor} />}
                activeDot={{ r: 5, fill: lineColor }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stats summary panel */}
      {stats && activeData.length >= 2 && (
        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border/30">
          {/* All-time best */}
          <div className="space-y-0.5">
            <div className="text-[9px] font-mono text-muted-foreground uppercase">All-Time Best</div>
            <div className="text-xs font-bold font-mono text-foreground">
              {mode === 'overload'
                ? `${stats.best.value} LBS`
                : `${stats.best.value.toLocaleString()} LBS`}
            </div>
            <div className="text-[9px] font-mono text-muted-foreground">
              {mode === 'overload'
                ? `${stats.best.bestWeight} × ${stats.best.bestReps} reps`
                : `vol — ${formatDate(stats.best.date)}`}
            </div>
          </div>

          {/* Sessions logged */}
          <div className="space-y-0.5 text-center">
            <div className="text-[9px] font-mono text-muted-foreground uppercase">Sessions</div>
            <div className="text-xs font-bold font-mono text-foreground">{stats.sessions}</div>
            <div className="text-[9px] font-mono text-muted-foreground">logged</div>
          </div>

          {/* Trend */}
          <div className="space-y-0.5 text-right">
            <div className="text-[9px] font-mono text-muted-foreground uppercase">Trend</div>
            <div
              className={`text-xs font-bold font-mono flex items-center justify-end gap-1 ${
                stats.trend === 'up'
                  ? 'text-green-400'
                  : stats.trend === 'down'
                  ? 'text-red-400'
                  : 'text-muted-foreground'
              }`}
            >
              {stats.trend === 'up' && <TrendingUp className="h-3 w-3" />}
              {stats.trend === 'down' && <TrendingDown className="h-3 w-3" />}
              {stats.trend === 'flat' && <Minus className="h-3 w-3" />}
              {stats.trend === 'up' ? 'Progressing' : stats.trend === 'down' ? 'Declining' : 'Maintaining'}
            </div>
            <div className="text-[9px] font-mono text-muted-foreground">last 3 sessions</div>
          </div>
        </div>
      )}
    </div>
  );
}
